<template>
  <div class="login-wrap">
    <!-- 左侧品牌展示区 -->
    <div class="login-left">
      <div class="left-glow left-glow-1"></div>
      <div class="left-glow left-glow-2"></div>
      <div class="left-grid"></div>

      <div class="brand-top">
        <div class="brand-logo">
          <img v-if="sysConfig.system_logo" :src="sysConfig.system_logo" alt="LOGO" />
          <el-icon v-else :size="34"><OfficeBuilding /></el-icon>
        </div>
        <span class="brand-name">{{ sysConfig.system_name || '粮库资产管理平台' }}</span>
      </div>

      <div class="brand-center">
        <h1 class="brand-title">{{ sysConfig.dashboard_name || '粮库资产可视化平台' }}</h1>
        <p class="brand-sub">智能粮库资产一体化管理平台</p>
        <div class="brand-feats">
          <div class="feat-item">
            <div class="feat-icon"><el-icon :size="20"><Monitor /></el-icon></div>
            <div class="feat-text">
              <div class="feat-t">资产全景监控</div>
              <div class="feat-d">库区 / 仓房 / 设备一图统揽</div>
            </div>
          </div>
          <div class="feat-item">
            <div class="feat-icon"><el-icon :size="20"><Bell /></el-icon></div>
            <div class="feat-text">
              <div class="feat-t">智能预警联动</div>
              <div class="feat-d">报警、工单、巡检闭环处置</div>
            </div>
          </div>
          <div class="feat-item">
            <div class="feat-icon"><el-icon :size="20"><VideoCamera /></el-icon></div>
            <div class="feat-text">
              <div class="feat-t">可视化大屏</div>
              <div class="feat-d">实时态势与合同执行一览</div>
            </div>
          </div>
        </div>
      </div>

      <div class="brand-bottom">
        <span>{{ sysConfig.copyright || '© 2024 粮库资产管理平台' }}</span>
      </div>
    </div>

    <!-- 右侧登录表单区 -->
    <div class="login-right">
      <div class="login-card">
        <div class="card-head">
          <div class="card-logo">
            <img v-if="sysConfig.system_logo" :src="sysConfig.system_logo" alt="LOGO" />
            <el-icon v-else :size="26"><OfficeBuilding /></el-icon>
          </div>
          <h2 class="card-title">欢迎登录</h2>
          <p class="card-sub">{{ sysConfig.system_name || '粮库资产管理平台' }}</p>
        </div>

        <el-form :model="loginForm" ref="loginFormRef" :rules="rules" @submit.prevent="handleLogin">
          <el-form-item prop="username">
            <el-input
              v-model="loginForm.username"
              placeholder="请输入用户名"
              size="large"
              :prefix-icon="User"
              @keyup.enter="handleLogin" />
          </el-form-item>
          <el-form-item prop="password">
            <el-input
              v-model="loginForm.password"
              type="password"
              placeholder="请输入密码"
              size="large"
              show-password
              :prefix-icon="Lock"
              @keyup.enter="handleLogin" />
          </el-form-item>
          <el-form-item>
            <el-button
              type="primary"
              class="login-btn"
              :loading="loading"
              native-type="submit"
              @click="handleLogin">登 录</el-button>
          </el-form-item>
        </el-form>

        <div class="card-foot">
          <span>{{ sysConfig.system_version ? '版本 ' + sysConfig.system_version : '' }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { User, Lock, OfficeBuilding, Monitor, Bell, VideoCamera } from '@element-plus/icons-vue'
import { login } from '../api'
import { sysConfig } from '../utils/sysConfig'

const router = useRouter()
const loginFormRef = ref(null)
const loading = ref(false)
const loginForm = reactive({
  username: '',
  password: ''
})

const rules = {
  username: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
  password: [{ required: true, message: '请输入密码', trigger: 'blur' }]
}

const handleLogin = async () => {
  if (!loginFormRef.value) return
  await loginFormRef.value.validate(async (valid) => {
    if (!valid) return
    loading.value = true
    try {
      const res = await login(loginForm)
      localStorage.setItem('token', res.data.token)
      localStorage.setItem('user', JSON.stringify(res.data.user))
      ElMessage.success('登录成功')
      router.push('/index')
    } catch (error) {
      ElMessage.error(error.message || '登录失败')
    } finally {
      loading.value = false
    }
  })
}
</script>

<style scoped>
.login-wrap {
  display: flex;
  height: 100vh;
  width: 100%;
  background: #0f1b2d;
}

/* ===== 左侧品牌区 ===== */
.login-left {
  position: relative;
  flex: 0 0 54%;
  overflow: hidden;
  background: linear-gradient(135deg, #13243d 0%, #1c3756 55%, #0f2238 100%);
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 48px 56px;
}

.left-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.55;
  pointer-events: none;
}
.left-glow-1 {
  width: 420px;
  height: 420px;
  top: -120px;
  right: -80px;
  background: radial-gradient(circle, #2f7bd6 0%, rgba(47,123,214,0) 70%);
}
.left-glow-2 {
  width: 360px;
  height: 360px;
  bottom: -100px;
  left: -60px;
  background: radial-gradient(circle, #1aa3a3 0%, rgba(26,163,163,0) 70%);
}

.left-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px);
  background-size: 40px 40px;
  mask-image: linear-gradient(180deg, rgba(0,0,0,0.6), rgba(0,0,0,0) 70%);
  -webkit-mask-image: linear-gradient(180deg, rgba(0,0,0,0.6), rgba(0,0,0,0) 70%);
  pointer-events: none;
}

.brand-top {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 2;
}
.brand-logo {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.brand-logo img {
  width: 40px;
  height: 40px;
  object-fit: contain;
}
.brand-name {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.brand-center {
  position: relative;
  z-index: 2;
  margin-top: -20px;
}
.brand-title {
  font-size: 40px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 14px;
  background: linear-gradient(90deg, #ffffff 0%, #9fd0ff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.brand-sub {
  font-size: 16px;
  color: rgba(255,255,255,0.72);
  margin: 0 0 48px;
  letter-spacing: 2px;
}

.brand-feats {
  display: flex;
  flex-direction: column;
  gap: 26px;
}
.feat-item {
  display: flex;
  align-items: center;
  gap: 16px;
}
.feat-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: rgba(64,158,255,0.15);
  border: 1px solid rgba(64,158,255,0.4);
  color: #6cb6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.feat-t {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}
.feat-d {
  font-size: 13px;
  color: rgba(255,255,255,0.6);
  margin-top: 3px;
}

.brand-bottom {
  position: relative;
  z-index: 2;
  font-size: 13px;
  color: rgba(255,255,255,0.45);
}

/* ===== 右侧表单区 ===== */
.login-right {
  flex: 1;
  background: #f0f2f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-card {
  width: 380px;
  max-width: 86%;
  background: #fff;
  border-radius: 16px;
  padding: 40px 36px 30px;
  box-shadow: 0 12px 40px rgba(20,40,70,0.12);
}

.card-head {
  text-align: center;
  margin-bottom: 32px;
}
.card-logo {
  width: 56px;
  height: 56px;
  margin: 0 auto 14px;
  border-radius: 14px;
  background: linear-gradient(135deg, #2a3f5f, #409eff);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.card-logo img {
  width: 48px;
  height: 48px;
  object-fit: contain;
}
.card-title {
  font-size: 24px;
  font-weight: 700;
  color: #1f2d40;
  margin: 0 0 6px;
}
.card-sub {
  font-size: 14px;
  color: #8a94a6;
  margin: 0;
}

.login-card :deep(.el-input__wrapper) {
  border-radius: 10px;
  height: 46px;
  box-shadow: 0 0 0 1px #dfe4ec inset;
}
.login-card :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px #409eff inset;
}

.login-btn {
  width: 100%;
  height: 46px;
  font-size: 16px;
  letter-spacing: 4px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #2a3f5f 0%, #409eff 100%);
  box-shadow: 0 8px 18px rgba(64,158,255,0.32);
  transition: all 0.25s ease;
}
.login-btn:hover {
  background: linear-gradient(135deg, #33507a 0%, #5aaeff 100%);
  box-shadow: 0 10px 24px rgba(64,158,255,0.45);
}

.card-foot {
  text-align: center;
  margin-top: 18px;
  font-size: 12px;
  color: #aab2c0;
  min-height: 16px;
}

/* ===== 响应式：窄屏隐藏品牌区 ===== */
@media (max-width: 1100px) {
  .login-left {
    display: none;
  }
  .login-right {
    flex: 1;
  }
}
</style>
